import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React from "react";
import { BASE_API_URL } from "store/Me";
import moment from "moment";
import { GuestType } from "./AdminGuests";
import { Button } from "components/ui/button";
import { AdminTable } from "components/shared/AdminTable";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";

type ActivitySource = "Hardware" | "MobileApp";

const NO_GUEST_TYPE_VALUE = "__no_guest_type__";
const NO_ACTIVITY_SOURCE_VALUE = "__no_activity_source__";
const FILTER_TOGGLE_GROUP_CLASS_NAME =
  "max-w-full flex-wrap gap-2 !border-0 !bg-transparent !p-0";
const FILTER_TOGGLE_ITEM_CLASS_NAME =
  "h-9 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-950 shadow-none hover:bg-slate-50 data-[state=on]:!border-slate-200 data-[state=on]:!bg-blue-100 data-[state=on]:!text-slate-950";

export default function GuestLockActivity({ guestId }: { guestId: string }) {
  const [page, setPage] = React.useState(0);
  const [limit, setLimit] = React.useState(10);
  const [guestTypeFilter, setGuestTypeFilter] = React.useState<
    GuestType | ""
  >("");
  const [sourceFilter, setSourceFilter] = React.useState<ActivitySource | "">(
    "",
  );
  const { data, isError } = useGetGuestLockActivity(
    guestId,
    page,
    limit,
    guestTypeFilter || undefined,
    sourceFilter || undefined,
  );

  if (isError) {
    return <div>There as an error loading key requests.</div>;
  }

  if (data) {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-6 mt-2">
          <div className="flex flex-wrap items-center gap-2">
            <span style={{ fontSize: 12 }}>Guest Type:</span>
            <ToggleGroup
              aria-label="Guest type"
              className={FILTER_TOGGLE_GROUP_CLASS_NAME}
              type="single"
              value={guestTypeFilter || NO_GUEST_TYPE_VALUE}
              onValueChange={(value) => {
                if (!value) return;
                setGuestTypeFilter(
                  value === NO_GUEST_TYPE_VALUE ? "" : (value as GuestType),
                );
                setPage(0);
              }}
            >
              <ToggleGroupItem
                className={FILTER_TOGGLE_ITEM_CLASS_NAME}
                value={NO_GUEST_TYPE_VALUE}
              >
                None
              </ToggleGroupItem>
              <ToggleGroupItem
                className={FILTER_TOGGLE_ITEM_CLASS_NAME}
                value={GuestType.Resident}
              >
                Resident
              </ToggleGroupItem>
              <ToggleGroupItem
                className={FILTER_TOGGLE_ITEM_CLASS_NAME}
                value={GuestType.Staff}
              >
                Staff
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span style={{ fontSize: 12 }}>Source:</span>
            <ToggleGroup
              aria-label="Activity source"
              className={FILTER_TOGGLE_GROUP_CLASS_NAME}
              type="single"
              value={sourceFilter || NO_ACTIVITY_SOURCE_VALUE}
              onValueChange={(value) => {
                if (!value) return;
                setSourceFilter(
                  value === NO_ACTIVITY_SOURCE_VALUE
                    ? ""
                    : (value as ActivitySource),
                );
                setPage(0);
              }}
            >
              <ToggleGroupItem
                className={FILTER_TOGGLE_ITEM_CLASS_NAME}
                value={NO_ACTIVITY_SOURCE_VALUE}
              >
                None
              </ToggleGroupItem>
              <ToggleGroupItem
                className={FILTER_TOGGLE_ITEM_CLASS_NAME}
                value="Hardware"
              >
                Hardware
              </ToggleGroupItem>
              <ToggleGroupItem
                className={FILTER_TOGGLE_ITEM_CLASS_NAME}
                value="MobileApp"
              >
                Mobile
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
        <AdminTable
          aria-label="Guest lock activity"
          containerClassName="mt-1 mb-2"
        >
          <thead>
            <tr>
              <th scope="col">Timestamp</th>
              <th scope="col">Lock Name</th>
              <th scope="col">Site</th>
              <th scope="col">Lock Activity Category</th>
              <th scope="col">Credential Number</th>
              <th scope="col">Credential Type</th>
              <th scope="col">Guest Type</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
              {data.data.map((lock, index) => (
                <tr key={`${lock.lockId}-${lock.timestamp}-${index}`}>
                  <td>
                    {moment(lock.timestamp).format("YYYY-MM-DD h:mm A")}
                  </td>
                  <td>{lock.lockName}</td>
                  <td>{lock.siteName}</td>
                  <td>{lock.lockActivityCategory}</td>
                  <td>{lock.credentialNumber}</td>
                  <td>{lock.credentialType}</td>
                  <td>{lock.guestType}</td>
                  <td>{lock.details}</td>
                </tr>
              ))}
          </tbody>
        </AdminTable>
        <ul className="flex items-center justify-end gap-3 p-0 mt-4 list-none">
          <li>
            <span>Rows per page: </span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(0);
              }}
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </li>
          <li>
            <Button
              onClick={() => setPage((prev) => prev - 1)}
              disabled={page === 0}
              type="button"
            >
              Prev
            </Button>
          </li>
          <li className="border border-blue-400">
            <span>
              Page{" "}
              <select
                value={page}
                onChange={(e) => setPage(parseInt(e.target.value, 10))}
              >
                {Array.from(
                  { length: Math.max(Math.ceil(data.totalCount / limit), 1) },
                  (_, i) => (
                    <option key={i} value={i}>
                      {i + 1}
                    </option>
                  ),
                )}
              </select>{" "}
              of {Math.max(Math.ceil(data.totalCount / limit), 1)}
            </span>
          </li>
          <li>
            <Button
              onClick={() => setPage((prev) => prev + 1)}
              disabled={page + 1 >= Math.ceil(data.totalCount / limit)}
              type="button"
            >
              Next
            </Button>
          </li>
        </ul>
      </div>
    );
  }

  return <div>loading...</div>;
}

export interface GuestLockActivityResponse {
  totalCount: number;
  data: {
    lockId: string;
    timestamp: string;
    lockName: string;
    siteName: string;
    lockActivityCategory: string;
    credentialNumber: string;
    credentialType: string;
    details: string;
    guestType: GuestType;
  }[];
}

async function getGuestLockActivity(
  guestId: string,
  page: number,
  limit: number,
  guestType?: GuestType,
  source?: ActivitySource,
) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(limit),
  });
  if (guestType) params.set("guestType", guestType);
  if (source) params.set("source", source);

  const url = `${BASE_API_URL}/api/guest/${guestId}/lock-activity?${params.toString()}`;
  const response = await axios.get<GuestLockActivityResponse>(url);

  return response.data;
}

function useGetGuestLockActivity(
  guestId: string,
  page: number,
  limit: number,
  guestType?: GuestType,
  source?: ActivitySource,
) {
  return useQuery({
    queryKey: [
      "guests",
      "lock-activity",
      guestId,
      page,
      limit,
      guestType,
      source,
    ],
    queryFn: () => getGuestLockActivity(guestId, page, limit, guestType, source),
  });
}
